import React, { useCallback, useRef } from "react";
import { Bar } from "react-chartjs-2";

import { BarElement, CategoryScale, Chart, ChartOptions, LinearScale, ScriptableContext, ScriptableScaleContext } from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";

import { useTranslation } from "utils";

import { CHART_COLORS } from "consts";

import { Flex, Icon, Tooltip, Typography } from "components";

Chart.register(BarElement, CategoryScale, LinearScale, ChartDataLabels);

export type ResilienceScoreData = {
  bars: { year: number; value: number | null }[];
  currentScore: number;
  deltaFromBaseline: number;
  baselineYear: number;
};

type ResilienceScoreProps = {
  data: ResilienceScoreData;
  reportingYear: number;
};

const MAX_SCORE = 10;
const PLACEHOLDER_HEIGHT = MAX_SCORE * 0.62;

const ResilienceBarChart = ({ bars, reportingYear }: { bars: ResilienceScoreData["bars"]; reportingYear: number }) => {
  const { t } = useTranslation("fieldTimeline");
  const patternRef = useRef<CanvasPattern | null>(null);

  const getHatchPattern = useCallback((canvasCtx: CanvasRenderingContext2D): CanvasPattern => {
    if (!patternRef.current) {
      const pc = document.createElement("canvas");
      pc.width = 5;
      pc.height = 5;
      const pCtx = pc.getContext("2d")!;
      pCtx.strokeStyle = CHART_COLORS["green-300"];
      pCtx.lineWidth = 1.5;
      pCtx.beginPath();
      pCtx.moveTo(0, 5);
      pCtx.lineTo(5, 0);
      pCtx.stroke();
      patternRef.current = canvasCtx.createPattern(pc, "repeat")!;
    }
    return patternRef.current;
  }, []);

  const barColor = useCallback(
    (ctx: ScriptableContext<"bar">): string | CanvasPattern => {
      const bar = bars[ctx.dataIndex];
      if (!bar || bar.value === null) return getHatchPattern(ctx.chart.ctx);
      return bar.year === reportingYear ? CHART_COLORS["green-400"] : CHART_COLORS["green-300"];
    },
    [bars, reportingYear, getHatchPattern]
  );

  const data = {
    labels: bars.map((b) => String(b.year)),
    datasets: [
      {
        data: bars.map((b) => b.value ?? PLACEHOLDER_HEIGHT),
        backgroundColor: barColor,
        hoverBackgroundColor: barColor,
        borderRadius: 4,
        barPercentage: 0.55,
        categoryPercentage: 0.9,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    events: [],
    layout: { padding: { top: 18 } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: {
        anchor: "end",
        align: "end",
        offset: 2,
        formatter: (_val, ctx: Context) => {
          const bar = bars[ctx.dataIndex];
          return bar?.value !== null ? String(bar?.value) : t("impactPanel.resilienceScore.noData", "No data");
        },
        color: (ctx: Context) => (bars[ctx.dataIndex]?.value !== null ? CHART_COLORS["green-400"] : CHART_COLORS["grey-500"]),
        font: (ctx: Context) => ({
          family: "dazzed",
          size: bars[ctx.dataIndex]?.value !== null ? 9.5 : 9,
          weight: bars[ctx.dataIndex]?.value !== null ? "bold" : "normal",
        }),
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: (ctx: ScriptableScaleContext) => (bars[ctx.index]?.year === reportingYear ? CHART_COLORS.black : CHART_COLORS["grey-500"]),
          font: (ctx: ScriptableScaleContext) => ({
            family: "dazzed",
            size: 9,
            weight: bars[ctx.index]?.year === reportingYear ? 700 : 400,
          }),
        },
      },
      y: {
        display: false,
        min: 0,
        max: MAX_SCORE,
      },
    },
    animation: { duration: 300 },
  };

  return (
    <div className="resilience-score-chart-wrapper">
      <Bar data={data} options={options} />
    </div>
  );
};

export const ResilienceScore = ({ data, reportingYear }: ResilienceScoreProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { bars, currentScore, deltaFromBaseline, baselineYear } = data;

  const barsWithData = bars.filter((b) => b.value !== null);
  const hasData = barsWithData.length > 0;
  const missingYears = bars.filter((b) => b.value === null).map((b) => b.year);
  const showFooter = missingYears.length > 0;
  const reportingYearHasData = reportingYear != null && barsWithData.some((b) => b.year === reportingYear);
  const showDelta = barsWithData.length >= 2 && reportingYearHasData;

  const deltaPrefix = deltaFromBaseline > 0 ? "+" : "";

  return (
    <div className="resilience-score">
      <Flex align="center" gap="0.375rem" className="resilience-score-header">
        <Typography variant="paragraph-m" bold className="resilience-score-title">
          {t("impactPanel.resilienceScore.title", "RESILIENCE SCORE")}
        </Typography>
        <Tooltip title={t("impactPanel.resilienceScore.tooltip", "Your farm's resilience score based on soil health and regenerative practices.")}>
          <span>
            <Icon iconName="tabler/info-circle" size="xs" className="resilience-score-info-icon" />
          </span>
        </Tooltip>
      </Flex>

      <Flex align="center" justify="space-between" className="resilience-score-value-row">
        <Flex align="baseline" gap="0.25rem">
          <Typography variant="h3" className="resilience-score-current-score">
            {hasData ? currentScore : "—"}
          </Typography>
          <Typography variant="paragraph-s" className="resilience-score-max">
            /10
          </Typography>
        </Flex>
        {showDelta && (
          <span className="resilience-score-delta-badge">
            {deltaPrefix}
            {deltaFromBaseline} {t("impactPanel.resilienceScore.since", "since {{year}}", { year: baselineYear })}
          </span>
        )}
      </Flex>

      <ResilienceBarChart bars={bars} reportingYear={reportingYear} />

      {showFooter && (
        <Flex align="center" gap="0.25rem" className="resilience-score-footer">
          <Icon iconName="tabler/info-circle" size="xs" className="resilience-score-footer-icon" />
          <Typography variant="paragraph-xs" className="resilience-score-footer-text">
            {t("impactPanel.resilienceScore.dataNotAvailable", { years: missingYears.join(", "), defaultValue: "{{years}} data not yet available" })}
          </Typography>
        </Flex>
      )}
    </div>
  );
};
